import { AdjustChip, AdjustChipGroup, AdjustGroupHeader, AdjustNote, AdjustReadout } from '../AdjustControlPrimitives.component'
import { BLUE_100, BLUE_600, GRAY_400, GRAY_800, WHITE } from 'constants/styling/theme'
import { Box, Stack } from '@mui/material'
import { frameRectOf, outputSize } from '../adjustGeometry'

import { FC } from 'react'
import LinkIcon from '@mui/icons-material/Link'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import { MUINumberField } from 'components/common/MUINumberField'
import { MUITooltip } from 'components/common/MUITooltip'
import { useAdjustEditor } from '../AdjustEditor.context'
import { useTranslation } from 'react-i18next'

const PRESETS = [1, 0.75, 0.5, 0.25]

/** Saved size after the crop: width / height with the crop's ratio locked, or percentage presets. Never enlarges. */
export const ResizeControls: FC<{ disabled?: boolean }> = ({ disabled = false }) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const { doc, base, apply } = useAdjustEditor()
  if (!base) return null

  const resize = doc.resize
  const frame = frameRectOf(doc.crop, base.size)
  const maxWidth = Math.max(1, Math.round(frame.width))
  const maxHeight = Math.max(1, Math.round(frame.height))
  const size = outputSize(doc.crop, resize, base.size)
  const clamp = (value: number, max: number) => Math.min(max, Math.max(1, Math.round(value)))

  const setWidth = (value: number | null | undefined) => {
    if (!value) return
    apply({ type: 'setResize', resize: { scale: clamp(value, maxWidth) / frame.width } }, 'resize:width')
  }

  const setHeight = (value: number | null | undefined) => {
    if (!value) return
    if (resize.lock) apply({ type: 'setResize', resize: { scale: clamp(value, maxHeight) / frame.height } }, 'resize:height')
    else apply({ type: 'setResize', resize: { height: clamp(value, maxHeight) } }, 'resize:height')
  }

  const toggleLock = () => apply({ type: 'setResize', resize: { lock: !resize.lock, height: resize.lock ? size.height : null } })

  return (
    <Stack gap="1.2rem">

      <Stack flexDirection="row" alignItems="flex-end" gap="0.8rem">

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <MUINumberField label={t('adjust.resize.width')} value={size.width} min={1} max={maxWidth} suffix="px" disabled={disabled} onChange={setWidth} />
        </Box>

        <MUITooltip content={t('adjust.resize.keep_ratio')}>
          <Box
            component="button"
            type="button"
            aria-pressed={resize.lock}
            aria-label={t('adjust.resize.keep_ratio')}
            disabled={disabled}
            onClick={toggleLock}
            sx={{
              flexShrink: 0,
              width: '4rem',
              height: '4rem',
              marginBottom: '0.2rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '0.6rem',
              border: `1px solid ${resize.lock ? BLUE_600 : GRAY_400}`,
              backgroundColor: resize.lock ? BLUE_100 : WHITE,
              color: GRAY_800,
              cursor: disabled ? 'not-allowed' : 'pointer',
              '& .MuiSvgIcon-root': { fontSize: '2rem' },
            }}
          >
            {resize.lock ? <LinkIcon /> : <LinkOffIcon />}
          </Box>
        </MUITooltip>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <MUINumberField label={t('adjust.resize.height')} value={size.height} min={1} max={maxHeight} suffix="px" disabled={disabled} onChange={setHeight} />
        </Box>

      </Stack>

      <AdjustGroupHeader title={t('adjust.resize.presets')} />

      <AdjustChipGroup ariaLabel={t('adjust.resize.presets')}>
        {PRESETS.map(preset => (
          <AdjustChip
            key={preset}
            label={preset === 1 ? t('adjust.resize.original') : `${Math.round(preset * 100)}%`}
            selected={resize.lock && Math.abs(resize.scale - preset) < 1e-6}
            disabled={disabled}
            onClick={() => apply({ type: 'setResize', resize: { scale: preset, lock: true, height: null } })}
          />
        ))}
      </AdjustChipGroup>

      <AdjustReadout label={t('adjust.resize.saved_size')} value={t('adjust.size_px', { width: size.width, height: size.height })} />

      <AdjustNote>{t('adjust.resize.note')}</AdjustNote>

    </Stack>
  )
}
